/**
 * Client price / discount / line-total columns for the merged offer table
 * (`offer-unified-table` capability, CU-86cbc3r8b). Ported from the retired
 * `pricing-tab-columns.tsx` onto `OfferUnitItem` (the widened offer unit
 * listing row, task 1.1) instead of the separate `OfferUnitPrice` row the
 * retired Wyceny tab used — the merged table sources price data from the
 * same listing as every other column instead of a second fetch.
 *
 * `OfferUnitItem` carries the unit's actual `currency` (CU-86cbehbbv fix —
 * previously missing, which made every price/discount edit silently reset
 * the unit's currency to PLN). Falls back to `OFFER_TABLE_DEFAULT_CURRENCY`
 * only when a unit has no price row yet (new unit, currency not set).
 *
 * Also builds the nine OFFER_PRICE_OVERRIDE_FIELD_IDS (@ooh/contracts)
 * formatka price columns (offer-formatka-prices, CU-86cbgnt38) — the
 * client-facing price fields a media unit's current cost record sources,
 * now editable per unit within an offer. The row's own `catalogPrice`/
 * `discountedPrice`/etc. values already carry the EFFECTIVE value (the
 * offer's own override when set, else the media unit's value — see
 * offer-unit-list-projection.ts), so this file only decides whether to
 * render that value as an editor or as plain text:
 * - None of the nine is ever masked — `cost.view` gates internal costs,
 *   never client prices, and all nine are client prices (permission-model
 *   correction, CU-86cbgnt38 round 4 — an earlier revision masked
 *   catalogPrice/discountedPrice/lastMinutePrice/starterPrice per the row's
 *   `costsWithheld` marker, which was wrong).
 * - Which of the nine a given row offers as editable at all is decided by
 *   `getOfferPriceOverrideFieldIds(row.mediaType)` — `dooh` gets four,
 *   every other media type gets all nine.
 */
import { PriceInput } from '@ooh/ui/components/price-input';
import { INTERACTIVE_CELL_CLASSNAME, type ColumnDef } from '@ooh/ui/components/data-table';
import {
  OFFER_PRICE_OVERRIDE_FIELD_IDS,
  getOfferPriceOverrideFieldIds,
  type OfferPriceOverrideFieldId,
} from '@ooh/contracts';
import type { TFunction } from 'i18next';
import { formatMoney, formatPercent, currencySymbol, PCT_SYMBOL } from './money-format';
import type { OfferUnitItem } from '../units-tab-columns';

/**
 * Fallback used only when a unit has no price row yet (new unit, currency
 * not set) — see this file's header doc comment.
 */
export const OFFER_TABLE_DEFAULT_CURRENCY = 'PLN';
const DEFAULT_CURRENCY = OFFER_TABLE_DEFAULT_CURRENCY;

interface ReadOnlyMoneyProps {
  value: number | null;
  currency: string;
  emptyLabel: string;
}

function ReadOnlyMoney({ value, currency, emptyLabel }: ReadOnlyMoneyProps) {
  if (value === null) {
    return (
      <span className="text-muted-foreground/60 italic text-xs" data-testid="pricing-cell-empty">
        {emptyLabel}
      </span>
    );
  }
  return (
    <span className="font-mono text-xs" data-testid="pricing-cell-money">
      {formatMoney(value, currency)}
    </span>
  );
}

/** Every field id `buildOfferUnitPriceColumns` may render a price column for. */
export type OfferPriceFieldId = 'clientPriceNet' | 'discountPercent' | OfferPriceOverrideFieldId;

/** The nine formatka price-override column ids — hidden by default like every other cost column (see units-tab-columns.tsx's seeding). */
export const OFFER_UNIT_PRICE_OVERRIDE_COLUMN_IDS: readonly OfferPriceOverrideFieldId[] =
  OFFER_PRICE_OVERRIDE_FIELD_IDS;

/**
 * Ids of the columns `buildOfferUnitPriceColumns` builds — exported so a
 * caller that needs to exclude price columns (e.g. the read-only sale-
 * confirmation dialog, record-sale-columns.tsx) can reuse this list instead
 * of re-listing the literal ids (mirrors `OFFER_UNIT_COST_COLUMN_IDS` in
 * offer-unit-cost-columns.tsx).
 */
export const OFFER_UNIT_PRICE_COLUMN_IDS: readonly string[] = [
  'clientPriceNet',
  'discountPercent',
  'lineTotal',
  ...OFFER_UNIT_PRICE_OVERRIDE_COLUMN_IDS,
];

export interface OfferUnitPriceColumnConfig {
  canEdit: boolean;
  /** Local overrides for instant client-side feedback, keyed `${unitId}:${field}`. */
  overrides: Map<string, number | null>;
  onPriceChange: (
    unitId: OfferUnitItem['id'],
    field: OfferPriceFieldId,
    value: number | null,
  ) => void;
  t: TFunction<'offers'>;
}

function effectiveValue(
  overrides: Map<string, number | null>,
  unitId: OfferUnitItem['id'],
  field: OfferPriceFieldId,
  fallback: number | null,
): number | null {
  const key = `${unitId}:${field}`;
  return overrides.has(key) ? (overrides.get(key) ?? null) : fallback;
}

/**
 * Builds one editable/read-only column per OFFER_PRICE_OVERRIDE_FIELD_IDS
 * entry — see this file's header doc comment for the masking/editability
 * rules. Extracted to keep `buildOfferUnitPriceColumns` under the
 * max-lines-per-function budget.
 */
function buildFormatkaPriceColumns({
  canEdit,
  overrides,
  onPriceChange,
  t,
}: OfferUnitPriceColumnConfig): ColumnDef<OfferUnitItem>[] {
  const emptyLabel = t('workspace.pricingTab.columns.emptyPrice');

  return OFFER_PRICE_OVERRIDE_FIELD_IDS.map((id): ColumnDef<OfferUnitItem> => {
    const label = t(`table.columns.${id}`, { ns: 'inventory' });

    return {
      id,
      label,
      align: 'right',
      width: 130,
      sortable: true,
      sortKey: id,
      cellClassName: INTERACTIVE_CELL_CLASSNAME,
      renderCell: (row: OfferUnitItem) => {
        const rawValue = (row[id] ?? null) as number | null;
        const value = effectiveValue(overrides, row.id, id, rawValue);
        const currency = row.currency ?? DEFAULT_CURRENCY;
        const editableForRow = canEdit && getOfferPriceOverrideFieldIds(row.mediaType).includes(id);

        const content = editableForRow ? (
          <PriceInput
            value={value}
            onChange={(v) => {
              const safe = v !== null && v < 0 ? null : v;
              onPriceChange(row.id, id, safe);
            }}
            currency={currencySymbol(currency)}
            placeholder={emptyLabel}
            aria-label={label}
            data-testid={`formatka-price-input-${id}-${row.id}`}
          />
        ) : (
          <ReadOnlyMoney value={value} currency={currency} emptyLabel={emptyLabel} />
        );

        return <span data-testid={`formatka-price-cell-${id}-${row.id}`}>{content}</span>;
      },
    };
  });
}

/**
 * Builds the editable `clientPriceNet`/`discountPercent` columns, the
 * client-side computed `lineTotal` column, and the nine formatka price
 * columns (see this file's header doc comment). Read-only for a caller
 * without edit rights (view-only callers see prices but no way to change
 * them).
 */
export function buildOfferUnitPriceColumns(
  config: OfferUnitPriceColumnConfig,
): ColumnDef<OfferUnitItem>[] {
  const { canEdit, overrides, onPriceChange, t } = config;
  const emptyLabel = t('workspace.pricingTab.columns.emptyPrice');

  return [
    {
      id: 'clientPriceNet',
      label: t('workspace.pricingTab.columns.clientPriceNet'),
      align: 'right',
      width: 150,
      sortable: true,
      sortKey: 'clientPriceNet',
      // INTERACTIVE_CELL_CLASSNAME ('relative z-[1]') so the inline price editor keeps
      // winning hit-tests over the row-link stretched-link overlay — see
      // units-tab-columns.tsx's checkbox/note columns for the same pattern.
      cellClassName: INTERACTIVE_CELL_CLASSNAME,
      renderCell: (row) => {
        const value = effectiveValue(
          overrides,
          row.id,
          'clientPriceNet',
          row.clientPriceNet ?? null,
        );

        const currency = row.currency ?? DEFAULT_CURRENCY;

        if (!canEdit) {
          return (
            <span data-testid={`price-cell-${row.id}`}>
              <ReadOnlyMoney value={value} currency={currency} emptyLabel={emptyLabel} />
            </span>
          );
        }

        return (
          <span data-testid={`price-cell-${row.id}`}>
            <PriceInput
              value={value}
              onChange={(v) => {
                const safe = v !== null && v < 0 ? null : v;
                onPriceChange(row.id, 'clientPriceNet', safe);
              }}
              currency={currencySymbol(currency)}
              placeholder={emptyLabel}
              aria-label={t('workspace.pricingTab.columns.clientPriceNetAriaLabel')}
              data-testid={`price-input-${row.id}`}
            />
          </span>
        );
      },
    },
    {
      id: 'discountPercent',
      label: t('workspace.pricingTab.columns.discountPercent'),
      align: 'right',
      width: 110,
      sortable: true,
      sortKey: 'discountPercent',
      // INTERACTIVE_CELL_CLASSNAME ('relative z-[1]') — see clientPriceNet above.
      cellClassName: INTERACTIVE_CELL_CLASSNAME,
      renderCell: (row) => {
        const value = effectiveValue(
          overrides,
          row.id,
          'discountPercent',
          row.discountPercent ?? null,
        );

        if (!canEdit) {
          if (value === null) {
            return (
              <span
                className="text-muted-foreground/60 italic text-xs"
                data-testid={`discount-cell-${row.id}`}
              >
                {emptyLabel}
              </span>
            );
          }
          return (
            <span className="font-mono text-xs" data-testid={`discount-cell-${row.id}`}>
              {formatPercent(value)}
            </span>
          );
        }

        return (
          <span data-testid={`discount-cell-${row.id}`}>
            <PriceInput
              value={value}
              onChange={(v) => {
                const safe = v !== null && v < 0 ? null : v;
                onPriceChange(row.id, 'discountPercent', safe);
              }}
              currency={PCT_SYMBOL}
              placeholder={emptyLabel}
              aria-label={t('workspace.pricingTab.columns.discountPercentAriaLabel')}
              data-testid={`discount-input-${row.id}`}
            />
          </span>
        );
      },
    },
    {
      id: 'lineTotal',
      label: t('workspace.pricingTab.columns.lineTotal'),
      align: 'right',
      width: 150,
      sortable: true,
      sortKey: 'lineTotal',
      // INTERACTIVE_CELL_CLASSNAME ('relative z-[1]') — see clientPriceNet above.
      cellClassName: INTERACTIVE_CELL_CLASSNAME,
      renderCell: (row) => {
        const price = effectiveValue(
          overrides,
          row.id,
          'clientPriceNet',
          row.clientPriceNet ?? null,
        );
        const discount = effectiveValue(
          overrides,
          row.id,
          'discountPercent',
          row.discountPercent ?? null,
        );

        if (price === null) {
          return (
            <span
              className="text-muted-foreground/60 italic text-xs"
              data-testid={`line-total-cell-${row.id}`}
            >
              {emptyLabel}
            </span>
          );
        }

        const lineTotal = price * (1 - (discount ?? 0) / 100);
        return (
          <span className="font-mono text-xs font-medium" data-testid={`line-total-cell-${row.id}`}>
            {formatMoney(lineTotal, row.currency ?? DEFAULT_CURRENCY)}
          </span>
        );
      },
    },
    ...buildFormatkaPriceColumns(config),
  ];
}
